/**
 * Locale configuration for the concerts-frontend.
 *
 * To add a new language:
 *   1. Add an entry to src/locales.json (code, label, shortLabel, intlLocale, flag).
 *   2. Add src/i18n/locales/{code}.json with the UI translations.
 *   3. Add src/assets/flags/{flag}.svg for the language switcher.
 *   4. Add the flag import + FLAG_MAP entry in src/i18n/languageOptions.js.
 *   5. Mirror the new code in frontend/src/locales.json (admin panel dropdowns).
 *   6. Add the new code to SUPPORTED_LOCALE_CODES in:
 *        backend/src/utils/eventLocaleHelpers.js
 *        backend/src/constants/locales.js
 */
import localeConfig from "../locales.json";

export const DEFAULT_LOCALE = localeConfig.defaultLocale;

/** @type {{ code: string, label: string, shortLabel: string, intlLocale: string, flag: string }[]} */
export const LOCALES = localeConfig.locales;

export const SUPPORTED_LOCALE_CODES = LOCALES.map((locale) => locale.code);

/** Dropdown options for admin forms */
export const LOCALE_OPTIONS = LOCALES.map((locale) => ({
  label: locale.label,
  value: locale.code
}));

/** Dropdown options for public language switcher (flags resolved separately) */
export const LOCALE_SWITCHER_OPTIONS = LOCALES.map((locale) => ({
  label: locale.shortLabel,
  value: locale.code,
  flag: locale.flag
}));

export function isSupportedLocale(value) {
  return SUPPORTED_LOCALE_CODES.includes(value);
}

export function normalizeLocale(value, fallback = DEFAULT_LOCALE) {
  return isSupportedLocale(value) ? value : fallback;
}

export function localeFromQuery(queryValue) {
  const raw = Array.isArray(queryValue) ? queryValue[0] : queryValue;
  return isSupportedLocale(raw) ? raw : null;
}

export function getIntlLocale(code) {
  return LOCALES.find((locale) => locale.code === code)?.intlLocale ?? DEFAULT_LOCALE;
}

const EVENT_EMPTY_FALLBACK_LOCALE = "en";

export function normalizeEnabledLocales(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((code) => isSupportedLocale(code));
}

/**
 * Resolve event language settings for API storage and public event pages.
 * Empty enabledLocales → English only.
 */
export function resolveEventLocales(enabledLocales, defaultLocale) {
  const enabled = normalizeEnabledLocales(enabledLocales);

  if (enabled.length === 0) {
    return {
      enabledLocales: [EVENT_EMPTY_FALLBACK_LOCALE],
      defaultLocale: EVENT_EMPTY_FALLBACK_LOCALE
    };
  }

  const resolvedDefault = enabled.includes(defaultLocale)
    ? defaultLocale
    : enabled.includes(normalizeLocale(defaultLocale))
      ? normalizeLocale(defaultLocale)
      : enabled[0];

  return {
    enabledLocales: enabled,
    defaultLocale: resolvedDefault
  };
}

export function resolveLandingLocale(enabledLocales, defaultLocale, queryLocale) {
  const resolved = resolveEventLocales(enabledLocales, defaultLocale);
  const query = localeFromQuery(queryLocale);

  if (query && resolved.enabledLocales.includes(query)) {
    return query;
  }

  return resolved.defaultLocale;
}
